import { useEffect, useState, useRef, useMemo } from 'react';
import { generatePath, useParams, useMatch, Outlet } from 'react-router-dom';
import {
  PermissionDenied,
  PermissionEnum,
  SchemaFilterType,
  Table,
  useFilters,
  useGetList,
  useQueryState,
} from '@ui/src';
import { useAppSelector } from '@store';
import { useLazyGetCustomerBeneficiariesQuery } from '../store/services/customers';
import {
  columns,
  columnsLabels,
  schema,
  filtersSchema,
} from './variables/list';
import { Beneficiary, CustomerType } from '../types';
import ROUTES from '../../../routes';
import getStatusName from './helpers/get-status-name';

import cn from './beneficiaries.module.css';

const formatBeneficiariesList = (list: Beneficiary[]): Beneficiary[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const paymentTypeArray = result.paymentType.split('_');

    const paymentType = paymentTypeArray
      ?.map((type) => `${type.charAt(0)}${type.slice(1).toLocaleLowerCase()}`)
      .join(' ');

    const render: Record<string, React.JSX.Element> = {
      beneficiaryType: (
        <div>
          {result.beneficiaryType === 'INDIVIDUAL' ? 'Individual' : 'Company'}
        </div>
      ),
      paymentType: (
        <div>
          {`${paymentType} ${
            result.currencyId ? `(${result.currency?.name})` : ''
          }`}
        </div>
      ),
      iban: (
        <div className={cn.ibanRow}>
          {result.iban || result.bankAccountNumber}
        </div>
      ),
      countContracts: <div>{result.countContracts.count}</div>,
      status: <div>{getStatusName(result.status)}</div>,
    };

    result.render = render;

    return result;
  });

  return nextList;
};

const Beneficiaries = ({ type }: { type: CustomerType }) => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const [takeCount, setTakeCount] = useState(0);

  const { filters, onFilterChange } = useQueryState(schema);

  const { customerId } = useParams();

  const match = useMatch(
    type === CustomerType.INDIVIDUAL
      ? ROUTES.CUSTOMERS_INDIVIDUAL_BENEFICIARIES
      : ROUTES.CUSTOMERS_CORPORATE_BENEFICIARIES,
  );

  const {
    customersDictionaries: { activePermissions },
    customerBeneficiaries: { beneficiaries, pagination },
    corporateCustomers: {
      info: {
        general: { baseInformation: corporateBaseInformation },
      },
    },
    individualCustomers: {
      info: {
        general: { baseInformation: individualBaseInformation },
      },
    },
  } = useAppSelector((state) => state);

  const [getBeneficiaries, { isLoading, isFetching }] =
    useLazyGetCustomerBeneficiariesQuery();

  const { getListAction, onFilter, setPage } = useGetList<Beneficiary>({
    getList: getBeneficiaries,
    onFilterChange,
    onSortChange: () => {},
    schema,
  });

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = (offsetHeight - 51) / 46;

        setTakeCount(Math.ceil(take - 3));
      }
    }
  }, [scrollRef]);

  const formattedData = useMemo(() => {
    const nextBeneficiaries = beneficiaries?.map((item) => ({
      ...item,
      url: generatePath(
        type === CustomerType.INDIVIDUAL
          ? ROUTES.CUSTOMERS_INDIVIDUAL_BENEFICIARY
          : ROUTES.CUSTOMERS_CORPORATE_BENEFICIARY,
        { customerId: customerId as string, beneficiaryId: item.id },
      ),
    }));

    const data = formatBeneficiariesList(nextBeneficiaries as Beneficiary[]);

    return data;
  }, [beneficiaries]);

  useEffect(() => {
    if (customerId && takeCount !== 0) {
      const filtersValues: any = {
        ...filters,
        organizationId: { value: [customerId], type: SchemaFilterType.STRING },
        take: { value: [takeCount.toString()], type: SchemaFilterType.STRING },
      };

      getListAction({ filters: filtersValues });
    }
  }, [customerId, takeCount]);

  useEffect(() => {
    let title: string;

    const companyName = localStorage.getItem('companyName');

    if (type === CustomerType.INDIVIDUAL) {
      if (
        individualBaseInformation?.firstName ||
        individualBaseInformation?.lastName
      ) {
        title = `${individualBaseInformation?.firstName} ${individualBaseInformation?.lastName}: Beneficiaries`;
      } else {
        title = 'Beneficiaries';
      }
    } else if (corporateBaseInformation?.name) {
      title = `${corporateBaseInformation?.name}: Beneficiaries`;
    } else {
      title = 'Beneficiaries';
    }

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [
    individualBaseInformation?.firstName,
    individualBaseInformation?.lastName,
    corporateBaseInformation?.name,
  ]);

  let content;

  if (!activePermissions?.includes(PermissionEnum.BENEFICIARIES_LIST)) {
    content = <PermissionDenied />;
  } else {
    content = (
      <>
        <div className={cn.beneficiariesTitle}>Beneficiaries</div>
        <Table
          columns={columns}
          data={formattedData || []}
          onSort={() => {}}
          filters={filters}
          sort={{}}
          columnsLabels={columnsLabels}
          tableClassName={!beneficiaries?.length ? cn.beneficiariesEmpty : ''}
          tableTopClassName={cn.beneficiaryTableTop}
          onFilterChange={(values) =>
            onFilter({
              ...values,
              organizationId: {
                value: [customerId as string],
                type: SchemaFilterType.STRING,
              },
              take: {
                value: [takeCount.toString()],
                type: SchemaFilterType.STRING,
              },
            })
          }
          filtersComponent={filtersComponent}
          handleSubmit={handleSubmit}
          isLoading={isLoading || isFetching}
          take={takeCount.toString()}
          setPage={(data) => {
            setPage({
              ...data,
              filters: {
                ...filters,
                organizationId: {
                  value: [customerId as string],
                  type: SchemaFilterType.STRING,
                },
              },
            });
          }}
          pagination={pagination}
          clickable
        />
      </>
    );
  }

  return (
    <div className={cn.beneficiariesWrapper} ref={scrollRef}>
      {match ? content : <div />}
      <Outlet />
    </div>
  );
};

export default Beneficiaries;
